import { RouteComponentProps, WindowLocation } from '@reach/router';
import { parse, stringify } from 'querystring';
import { useCallback } from 'react';

/**
 * Manipulates parameters in the URL query string as if they were state
 *
 * @param paramName - Parameter to parse from the URL
 * @param routeComponentProps - Props object passed from Reach Router.
 *                              (If your component is rendered as a route, this is a subset of its props)
 */
export function useQueryParam(
    paramName: string,
    routeComponentProps: Pick<RouteComponentProps, 'navigate' | 'location'>,
): [string | string[] | undefined, (newValue?: string | string[]) => void] {

    const {
        navigate,
        location: {
            pathname = location.pathname,
            search = location.search,
            hash = location.hash,
        } = ({} as WindowLocation),
    } = routeComponentProps;

    const query = parse(search.replace(/^\?/, ''));

    const setQueryArg = useCallback(
        (newValue?: string | string[]) => {
            const newQuery = { ...query };

            if (newValue !== undefined) {
                newQuery[paramName] = newValue;
            } else {
                delete newQuery[paramName];
            }

            navigate && navigate(pathname + '?' + stringify(newQuery) + hash);
        },
        [hash, navigate, pathname, paramName, query]
    );

    return [query[paramName], setQueryArg];
}
